import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useShallow } from 'zustand/react/shallow';

import { SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE } from '~/constants';
import { useUserInfoStore } from '~/store';
import { TypeSubmitSpecialMedicalInputForm } from '~/types';

/** @description 특수건강검진 관련 상태 훅 */
export const useSpecialMedicalStatus = (methods: UseFormReturn<TypeSubmitSpecialMedicalInputForm>) => {
  const { status, secondExaminationInProgress } = useUserInfoStore(
    useShallow((state) => ({
      status: state.specialMedicalExamination?.status ?? null,
      secondExaminationInProgress: state.specialMedicalExamination?.secondExaminationInProgress ?? false,
    }))
  );

  const isReSubmit = methods?.watch('isReSubmit') ?? false;

  const code = status?.code ?? '';

  const isRegistered = useMemo(() => {
    return [
      SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.REGISTERED,
      SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.SECOND_REGISTERED,
    ].includes(code);
  }, [code]);

  const isApprovalPending = useMemo(() => {
    return [
      SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.APPROVAL_PENDED,
      SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.SECOND_APPROVAL_PENDED,
    ].includes(code);
  }, [code]);

  const isRejected = useMemo(() => {
    return [
      SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.REJECTED,
      SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.SECOND_REJECTED,
    ].includes(code);
  }, [code]);

  const isApproved = useMemo(() => {
    return [
      SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.APPROVED,
      SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.SECOND_APPROVED,
    ].includes(code);
  }, [code]);

  const isSecondSpecialMedical = useMemo(() => {
    return secondExaminationInProgress && code.includes('SECOND');
  }, [code]);

  const isEditable = useMemo(() => {
    return isRegistered || (isRejected && isReSubmit);
  }, [isRegistered, isRejected, isReSubmit]);

  const submitButtonConfig: { text: string; color: 'primary' | 'error' } | null = useMemo(() => {
    if (isEditable) {
      return { text: '제출', color: 'primary' };
    }
    if (isApprovalPending) {
      return { text: '제출 취소', color: 'error' };
    }
    if (isRejected) {
      return { text: '다시 제출', color: 'primary' };
    }
    return null;
  }, [isApprovalPending, isRejected, isEditable]);

  return {
    isRegistered,
    isApprovalPending,
    isRejected,
    isApproved,
    isSecondSpecialMedical,
    isEditable,
    submitButtonConfig,
  };
};
